import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, authState, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, ActionCodeSettings } from '@angular/fire/auth';
import { Firestore, doc, getDocFromServer, updateDoc, DocumentSnapshot } from '@angular/fire/firestore';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import type { User } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';
import { ResidencialStateService } from './residencial-state.service';
import { ResidenteStateService } from './residente-state.service';
import { GuardiaStateService } from './guardia-state.service';
import { SuperadminStateService } from './superadmin-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth                  = inject(Auth);
  private firestore             = inject(Firestore);
  private translate             = inject(TranslateService);
  private router                = inject(Router);
  private residencialStateSvc   = inject(ResidencialStateService);
  private residenteStateSvc     = inject(ResidenteStateService);
  private guardiaStateSvc       = inject(GuardiaStateService);
  private superadminStateSvc    = inject(SuperadminStateService);

  // Observable del estado de autenticación de Firebase — usado por guards para proteger rutas.
  getUser(): Observable<User | null> {
    return authState(this.auth);
  }

  /**
   * Flujo de login:
   * 1. Firebase Auth valida credenciales y devuelve el token.
   * 2. Se fuerza refresco del token para garantizar que los custom claims estén presentes.
   * 3. El rol se lee desde los claims del token (fuente de verdad en producción).
   *    En emulador, fallback a Firestore porque los claims se asignan manualmente.
   * 4. Se lee usuarios/{uid} una sola vez — el snapshot se reutiliza en los pasos siguientes.
   * 5. Se verifica activo y permitirLogin; si alguno es false se fuerza signOut y se aborta.
   * 6. syncIdioma persiste el idioma seleccionado en Firestore y localStorage.
   * 7. Según el rol, se carga el state service correspondiente con el snapshot ya leído.
   *    Estos datos quedan en memoria y localStorage para sobrevivir recargas de página.
   * 8. Devuelve el rol — el componente de login usa esto para navegar al módulo correcto.
   */
  async login(email: string, password: string, selectedIdioma: string): Promise<string> {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);

    // Sin este refresco, un token cacheado sin claims causa 403 en Storage.
    await credential.user.getIdToken(true);

    const tokenResult = await credential.user.getIdTokenResult();
    let   rol         = tokenResult.claims['rol'] as string | undefined;

    // Lectura única compartida por el fallback de emulador, la validación de estado
    // y los state services — evita leer el mismo documento varias veces por login.
    const userSnap = await getDocFromServer(doc(this.firestore, `usuarios/${credential.user.uid}`));

    // En el emulador los custom claims se asignan manualmente, por lo que pueden no estar
    // presentes en el token. Se lee el rol directamente desde el documento de Firestore.
    if (!rol && !environment.production) {
      rol = userSnap.data()?.['rol'] as string | undefined;
    }

    if (!rol) throw { code: 'auth/no-role' };

    // activo:false = soft delete | permitirLogin:false = acceso bloqueado por el admin
    if (userSnap.exists()) {
      const data = userSnap.data() as { activo?: boolean; permitirLogin?: boolean };
      if (data.activo === false || data.permitirLogin === false) {
        await signOut(this.auth);
        throw { code: 'auth/user-disabled' };
      }
    }

    const idioma = await this.syncIdioma(credential.user.uid, userSnap, selectedIdioma);
    this.translate.use(idioma).subscribe();

    // El snapshot ya leído se pasa a cada state service para evitar una segunda lectura.
    // Deben ejecutarse antes de navegar al módulo para evitar pantallas vacías.
    if (rol === 'superadmin') await this.superadminStateSvc.loadForSuperadmin(credential.user.uid, userSnap);
    if (rol === 'admin')      await this.residencialStateSvc.loadForAdmin(credential.user.uid, userSnap);
    if (rol === 'residente')  await this.residenteStateSvc.loadForResidente(credential.user.uid, userSnap);
    if (rol === 'guardia')    await this.guardiaStateSvc.loadForGuardia(credential.user.uid, userSnap);

    return rol;
  }

  // Limpia todos los state services antes de cerrar sesión en Firebase para evitar
  // que datos del tenant anterior queden en memoria si otro usuario inicia sesión después.
  async logout(): Promise<void> {
    this.superadminStateSvc.clearState();
    this.residencialStateSvc.clearState();
    this.residenteStateSvc.clearState();
    this.guardiaStateSvc.clearState();
    await signOut(this.auth);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Envía al correo del usuario un link para que establezca su contraseña.
   * Firebase usa la plantilla nativa configurada en Authentication → Templates,
   * traducida según el idioma indicado.
   * Reutilizable para invitaciones de admins, residentes y guardias.
   */
  async enviarLinkEstablecerPassword(email: string, idioma: 'es' | 'en'): Promise<void> {
    this.auth.languageCode = idioma;

    const actionCodeSettings: ActionCodeSettings = {
      url: `${window.location.origin}/auth/reset-password`,
      handleCodeInApp: false
    };

    await sendPasswordResetEmail(this.auth, email, actionCodeSettings);
  }

  // Persiste el idioma seleccionado en Firestore (si cambió) y en localStorage.
  // Si el documento no existe, solo persiste en localStorage.
  private async syncIdioma(uid: string, userSnap: DocumentSnapshot, selectedIdioma: string): Promise<string> {
    if (userSnap.exists()) {
      const data = userSnap.data() as { idioma?: string };
      if (!data.idioma || data.idioma !== selectedIdioma) {
        await updateDoc(doc(this.firestore, `usuarios/${uid}`), { idioma: selectedIdioma });
      }
    }
    localStorage.setItem('rc_idioma', selectedIdioma);
    return selectedIdioma;
  }
}
